/**
 * FoodMapPage — Interactive map showing nearby restaurants + user posts
 * Uses Google Maps JS API (already loaded in index.html)
 * Route: /map
 */
import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, MapPin, Star, Sparkles, List } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLocation } from '../contexts/LocationContext';
import DirectionsButton from '../components/DirectionsButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FoodMapPage = () => {
  const { location: userLoc, locationString } = useLocation();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [showList, setShowList] = useState(false);

  const lat = userLoc?.lat || -33.8688;
  const lng = userLoc?.lng || 151.2093;

  useEffect(() => {
    // Fetch nearby places
    fetch(`${API_URL}/api/places/nearby?lat=${lat}&lng=${lng}&limit=20`)
      .then(r => r.json())
      .then(d => setPlaces(d.places || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [lat, lng]);

  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;
    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat, lng },
      zoom: 14,
      disableDefaultUI: true,
      zoomControl: true,
      styles: [
        { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
        { featureType: 'poi.attraction', stylers: [{ visibility: 'off' }] },
      ],
    });
    mapInstanceRef.current = map;

    // User location marker
    new window.google.maps.Marker({
      position: { lat, lng },
      map,
      icon: { path: window.google.maps.SymbolPath.CIRCLE, fillColor: '#FF6B35', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 3, scale: 10 },
      title: 'You',
    });

    // Place markers
    places.forEach(place => {
      if (!place.location?.lat) return;
      const marker = new window.google.maps.Marker({
        position: { lat: place.location.lat, lng: place.location.lng },
        map,
        icon: { path: window.google.maps.SymbolPath.CIRCLE, fillColor: '#F4C430', fillOpacity: 0.9, strokeColor: '#fff', strokeWeight: 2, scale: 8 },
        title: place.name,
      });
      marker.addListener('click', () => setSelectedPlace(place));
    });
  }, [places, lat, lng]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-20 md:pb-8">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" className="p-1.5 hover:bg-gray-100 rounded-full"><ArrowLeft className="w-5 h-5 text-gray-600" /></Link>
          <div>
            <h1 className="text-base font-bold text-gray-900">Food Map</h1>
            <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3 text-[#FF6B35]" />{locationString || 'Set location'}</p>
          </div>
        </div>
        <button onClick={() => setShowList(!showList)} className={`p-2 rounded-xl transition-colors ${showList ? 'bg-[#FF6B35] text-white' : 'bg-gray-100 text-gray-600'}`}>
          <List className="w-5 h-5" />
        </button>
      </div>

      <div className="flex" style={{ height: 'calc(100vh - 120px)' }}>
        {/* Map */}
        <div ref={mapRef} className={`${showList ? 'hidden md:block md:flex-1' : 'flex-1'} bg-gray-200`} />

        {/* List view / Selected place */}
        {(showList || selectedPlace) && (
          <div className={`${showList ? 'w-full md:w-80' : 'w-80'} bg-white overflow-y-auto border-l border-gray-100`}>
            {selectedPlace ? (
              <div className="p-4">
                <button onClick={() => setSelectedPlace(null)} className="text-xs text-gray-500 hover:text-gray-700 mb-3 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back to list</button>
                {selectedPlace.photo_url && <img src={selectedPlace.photo_url} alt={selectedPlace.name} className="w-full h-40 object-cover rounded-2xl mb-3" />}
                <h2 className="text-lg font-bold text-gray-900">{selectedPlace.name}</h2>
                <p className="text-sm text-gray-500 mb-2">{selectedPlace.address}</p>
                <div className="flex items-center gap-3 text-sm text-gray-600 mb-3">
                  {selectedPlace.rating && <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-[#F4C430] text-[#F4C430]" />{selectedPlace.rating}</span>}
                  {selectedPlace.price_symbol && <span>{selectedPlace.price_symbol}</span>}
                  {selectedPlace.distance_km && <span>{selectedPlace.distance_km} km</span>}
                </div>
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedPlace.name)}&query_place_id=${selectedPlace.place_id}`} target="_blank" rel="noopener noreferrer"
                  className="w-full py-2.5 bg-gray-900 text-white font-semibold text-sm rounded-2xl flex items-center justify-center gap-1.5 hover:bg-gray-800 transition-colors mb-2">
                  <MapPin className="w-4 h-4" /> View on Google Maps
                </a>
                <DirectionsButton name={selectedPlace.name} address={selectedPlace.address} lat={selectedPlace.location?.lat} lng={selectedPlace.location?.lng} className="w-full justify-center py-2 bg-gray-100 rounded-2xl text-sm font-semibold" />
              </div>
            ) : (
              <div className="p-3 space-y-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-1 mb-2">{places.length} places nearby</p>
                {loading ? (
                  [1,2,3,4].map(i => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)
                ) : places.map(p => (
                  <button key={p.place_id} onClick={() => { setSelectedPlace(p); setShowList(false); }}
                    className="w-full text-left p-3 rounded-xl hover:bg-gray-50 transition-colors flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {p.photo_url ? <img src={p.photo_url} alt="" className="w-full h-full object-cover" /> : <MapPin className="w-5 h-5 text-gray-400" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">{p.name}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        {p.rating && <span className="flex items-center gap-0.5"><Star className="w-3 h-3 fill-[#F4C430] text-[#F4C430]" />{p.rating}</span>}
                        {p.price_symbol && <span>{p.price_symbol}</span>}
                        {p.distance_km && <span>{p.distance_km}km</span>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FoodMapPage;
